/* =========================================================
   IDENTITY LUXE — Mini cart drawer (2026-10)
   - window.ilMiniCart.open(html?)  → opens the drawer (html = rendered
     "il-mini-cart" section from a /cart/add response, else it is fetched)
   - quantity / remove buttons → /cart/change.js, re-rendered server side
   - stays in sync with the Family Pack builder and the free tote script
   ========================================================= */
(() => {
  if (customElements.get('il-mini-cart')) return;

  const SECTION = 'il-mini-cart';
  const BUBBLE = 'cart-icon-bubble';
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  class ILMiniCart extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.box = this.querySelector('[data-il-mc-slot]');
      this.panel = this.querySelector('.il-mc__panel');
      window.ilMiniCart = this;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-il-mc-close]')) { e.preventDefault(); this.close(); return; }
        const qty = e.target.closest('[data-il-mc-qty]');
        if (qty) {
          const item = qty.closest('[data-key]');
          if (item) this.change(item.dataset.key, Number(qty.dataset.ilMcQty));
        }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });

      // other scripts changed the cart (Family Pack builder, free tote)
      const sync = (e) => {
        if (!this.isOpen) return;
        if (e && e.detail && e.detail.source === 'il-mini-cart') return;
        this.refresh();
      };
      document.addEventListener('cart:update', sync);
      document.addEventListener('il:cart-changed', sync);
    }

    get isOpen() { return !this.hidden && this.classList.contains('is-open'); }

    url() {
      const base = (window.routes && window.routes.cart_url) || '/cart';
      return `${base}?sections=${SECTION}`;
    }

    setContent(html, justAdded) {
      if (!html) return;
      const inner = parse(html).querySelector('[data-il-mc-content]');
      if (!inner) return;
      this.box.replaceChildren(inner);
      if (window.ilCurrencyAr) window.ilCurrencyAr(inner);
      const added = inner.querySelector('[data-il-mc-added]');
      if (added) added.hidden = !justAdded;
    }

    updateBubble(sections) {
      const html = sections && sections[BUBBLE];
      const bubble = document.getElementById('cart-icon-bubble');
      if (!html || !bubble) return;
      const el = parse(html).querySelector('.shopify-section');
      if (el) bubble.innerHTML = el.innerHTML;
    }

    open(html) {
      this.hidden = false;
      if (html) this.setContent(html, true);
      else this.refresh();
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('overflow-hidden');
        this.panel.focus({ preventScroll: true });
      });
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      clearTimeout(this._t);
      this._t = setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 380);
    }

    refresh() {
      return fetch(this.url(), { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((sections) => this.setContent(sections[SECTION], false))
        .catch(() => {});
    }

    change(key, quantity) {
      if (this._busy) return;
      this._busy = true;
      this.classList.add('is-busy');
      fetch((window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, quantity), sections: `${SECTION},${BUBBLE}` })
      })
        .then((r) => r.json().then((json) => ({ ok: r.ok, json })))
        .then(({ ok, json }) => {
          if (!ok || json.status) {
            const err = this.querySelector('[data-il-mc-error]');
            if (err) { err.textContent = json.description || json.message || ''; err.hidden = false; }
            return;
          }
          this.setContent(json.sections && json.sections[SECTION], false);
          this.updateBubble(json.sections);
          document.dispatchEvent(new CustomEvent('il:cart-changed', { detail: { source: 'il-mini-cart', cart: json } }));
          try {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'il-mini-cart', cartData: json });
            }
          } catch (e) { /* ignore */ }
        })
        .catch(() => this.refresh())
        .finally(() => { this._busy = false; this.classList.remove('is-busy'); });
    }
  }

  customElements.define('il-mini-cart', ILMiniCart);
})();
